import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle, ArrowRight, Clock, PhoneCall } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LabourRegistrySuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      // clear preserved form on success path
      poll(0);
    } else {
      setStatus("timeout");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const poll = async (n) => {
    if (n >= 6) {
      setStatus("timeout");
      return;
    }
    try {
      const res = await axios.get(`${API}/labour-registry/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        sessionStorage.removeItem("registry_form");
        setInfo(res.data);
        setStatus("success");
        return;
      }
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    } catch (e) {
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-16 md:py-24" data-testid="registry-success-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="bg-white border border-slate-200 shadow-xl overflow-hidden">
          <div className="py-8" style={{ backgroundColor: "#0f172a" }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
              className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto"
            >
              <CheckCircle className="w-14 h-14" style={{ color: "#fb3700" }} />
            </motion.div>
          </div>
          <CardContent className="p-8 text-center">
            {status === "checking" && (
              <>
                <h1 className="text-2xl font-bold text-slate-900 mb-4">Verifying Payment…</h1>
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
                </div>
              </>
            )}

            {status === "success" && (
              <>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4" data-testid="registry-success-title">
                  Thank You — Registration Received!
                </h1>
                <p className="text-slate-600 mb-8">
                  {info?.applicant?.full_name ? `${info.applicant.full_name}, your ` : "Your "}
                  {info?.applicant?.tier ? <strong>{info.applicant.tier}</strong> : null} registration and
                  payment have been received. ProToCall will review and activate your listing within
                  1–2 business days.
                </p>
                <div className="bg-slate-50 rounded-lg p-6 mb-8 text-left space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#fb3700" }} />
                    <span className="text-slate-600">Your listing is activated within 1–2 business days.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <PhoneCall className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#fb3700" }} />
                    <span className="text-slate-600">When a company needs your trade, ProToCall contacts you first.</span>
                  </div>
                </div>
                <Button asChild className="text-white font-semibold h-12 px-6 rounded-full" style={{ backgroundColor: "#0f172a" }} data-testid="registry-home-btn">
                  <Link to="/">Back to Home <ArrowRight className="ml-2 w-4 h-4" /></Link>
                </Button>
              </>
            )}

            {status === "timeout" && (
              <>
                <h1 className="text-2xl font-bold text-slate-900 mb-4">Payment Processing</h1>
                <p className="text-slate-600 mb-8">
                  We're still confirming your payment. If you completed payment, your registration is
                  recorded — ProToCall will be in touch. Otherwise, please try again.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild variant="outline" className="border-2 border-slate-900 h-12 px-6 rounded-full">
                    <Link to="/labour-registry">Back to Registry</Link>
                  </Button>
                  <Button asChild className="bg-slate-900 text-white h-12 px-6 rounded-full">
                    <Link to="/contact">Contact Support</Link>
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
